import { type RefObject, useEffect, useMemo, useRef } from 'react';
import { addError } from '@infra/datadog/tracking';
import { TimeSyncService } from '@services/TimeSyncService';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { Chess } from 'chess.js';
import { proxy, useSnapshot } from 'valtio';

import { type ChessBoardProps } from '@exports/ChessBoard';
import { ChessGame, type ChessGameProps } from '@exports/ChessGame';
import { type MoveInfo } from '@exports/ChessGame/ChessGame';
import { ChessWinner, PromotionPiece, SquareName } from '@libs/chess/types';
import { ApiError } from '@libs/errors';
import { ISocketEvents } from '@libs/socket';

import { IGame } from '@features/game/types/game';
import { reduceGame } from '@features/game/utils/gameReducer';
import { COMMON_QUERY_KEYS } from '@features/shared/api';
import { components, useGameMoveV2Mutation } from '@features/shared/api/game';
import { GamesSocket, IGameSocketEvents } from '@features/shared/api/ws';
import { useDeviceId } from '@features/shared/hooks/useDeviceId';

/*
    ____,-------------------------------,____
    \   |             Types            |   /
    /___|-------------------------------|___\
*/

const MOVE_REQUEST_DEADLINE = 5 * 1000;
const MOVE_REQUEST_PROBLEMS_THRESHOLD = 3 * 1000;

type IMoveFn = ReturnType<typeof useGameMoveV2Mutation>['mutate'];

export type Props = {
  mode: 'tournament' | 'fast-game' | 'spectator';
  game: IGame;
  playerId?: string;
  anonToken?: string;
  historyMove?: number;
  onError?: (error: Error) => void;
  onRefetch?: (() => void) | (() => Promise<unknown>);
  onNetworkProblems?: (v: boolean) => void;
  //
  allowPremove?: boolean;
  autoPromotion?: ChessGameProps['autoPromotion'];
  boardSounds?: ChessGameProps['boardSounds'];
  //
  className?: ChessBoardProps['className'];
  theme?: ChessBoardProps['theme'];
  orientation?: ChessBoardProps['orientation'];
  notation?: ChessBoardProps['notation'];
  noAnimation?: ChessBoardProps['noAnimation'];
  noShadow?: ChessBoardProps['noShadow'];
  noAutoBorderRadius?: ChessBoardProps['noAutoBorderRadius'];
  noBorderRadius?: ChessBoardProps['noBorderRadius'];
  modal?: ChessBoardProps['modal'];
  valuation?: ChessBoardProps['valuation'];
  valuationValue?: ChessBoardProps['valuationValue'];
  arrows?: ChessBoardProps['arrows'];
  onUserArrow?: ChessGameProps['onUserArrow'];
};

/*
    ____,-------------------------------,____
    \   |             State            |   /
    /___|-------------------------------|___\
*/

class State {
  fen = '';
  lastMove: MoveInfo | null = null;
  premove: MoveInfo | null = null;
  problemMoveFen: string | null = null;
  socketProblems = false;
}

/*
    ____,-------------------------------,____
    \   |           Component          |   /
    /___|-------------------------------|___\
*/

export function ServerGameWidget(props: Props) {
  const {
    mode,
    game,
    playerId,
    anonToken,
    historyMove,
    onError: _onError,
    onRefetch: _onRefetch,
    onNetworkProblems,
    //
    allowPremove,
    autoPromotion,
    boardSounds,
    //
    orientation,
    className,
    theme,
    notation,
    noAnimation,
    noShadow,
    noAutoBorderRadius,
    noBorderRadius,
    modal,
    valuation,
    valuationValue,
    arrows,
    onUserArrow,
  } = props;

  const pRef = useRef(props);
  pRef.current = props;

  const queryClient = useQueryClient();
  const moveMutation = useGameMoveV2Mutation({ customAccessToken: anonToken });

  const playerSide = getSide(playerId, game);
  const isPlaying = playerSide !== 'none';
  const side = isPlaying && game.result === 'InProgress' ? playerSide : 'none';
  const winner = getWinner(game);

  // ---- Store ----

  const store = useMemo(
    () => (
      game.id /* sic, просто для пересоздания */,
      new Store(pRef, queryClient, moveMutation.mutate)
    ),
    [game.id, moveMutation.mutate, queryClient],
  );

  store.update();

  // ---- Socket ----

  useGamesSocket({
    mode,
    gameId: game.id,
    seq: game.seq,
    enabled: game.status === 'InProgress' || game.status === 'NotStarted',
    customToken: anonToken,
    cb: store.handleSocketEvent,
  });

  // ---- Snapshot ----

  const snap = useSnapshot(store.state) as State;

  // ---- Network problems (propagate to parent via effect) ----

  const hasNetworkProblems =
    snap.socketProblems || Boolean(snap.problemMoveFen && snap.problemMoveFen === game.currentFen);

  useEffect(() => {
    onNetworkProblems?.(hasNetworkProblems);
  }, [hasNetworkProblems, onNetworkProblems]);

  // ---- History (scoresheet navigation) ----

  const isHistory = historyMove !== undefined && historyMove !== game.moves.length - 1;
  const historyData = isHistory ? game.moves[historyMove] : undefined;

  // ---- Render ----

  return (
    <ChessGame
      key={game.id}
      fen={historyData ? historyData.fenAfter : snap.fen}
      side={historyData ? 'none' : side}
      lastMove={
        historyData
          ? {
              from: historyData.from as SquareName,
              to: historyData.to as SquareName,
            }
          : (snap.lastMove ?? undefined)
      }
      premove={historyData ? undefined : (snap.premove ?? undefined)}
      winner={historyData ? undefined : winner}
      silent={
        historyMove !==
        undefined /* sic! если использовать isHistory, то навигация на последний ход будет подзвучена */
      }
      allowPremove={allowPremove}
      autoPromotion={autoPromotion}
      boardSounds={boardSounds}
      blind={game.isBlind && game.status === 'InProgress'}
      onMove={store.handlePlayerMove}
      onPremove={store.handlePremove}
      orientation={orientation ?? (playerSide === 'none' ? 'white' : playerSide)}
      //
      className={className}
      theme={theme}
      notation={notation}
      noAnimation={noAnimation}
      noShadow={noShadow}
      noAutoBorderRadius={noAutoBorderRadius}
      noBorderRadius={noBorderRadius}
      modal={modal}
      valuation={valuation}
      valuationValue={valuationValue}
      arrows={arrows}
      onUserArrow={onUserArrow}
    />
  );
}

/*
    ____,-------------------------------,____
    \   |             Store            |   /
    /___|-------------------------------|___\
*/

class Store {
  state: State;

  private game: IGame;
  private lastMoveReceivedTime: number | null = null;
  private isRealtime = false;

  constructor(
    private pRef: RefObject<Props>,
    private queryClient: QueryClient,
    private moveFn: IMoveFn,
  ) {
    this.game = pRef.current.game;
    this.state = proxy(new State());
    this.syncFromGame(this.game);
  }

  private get props() {
    return this.pRef.current;
  }

  private get gameId() {
    return this.props.game.id;
  }

  private get isPlaying() {
    return getSide(this.props.playerId, this.game) !== 'none';
  }

  // ---- Sync from React ----

  update() {
    const next = this.props.game;
    const prev = this.game;
    this.game = next;

    if (next.moves.length > prev.moves.length) {
      this.handleServerMove(next);
    }
  }

  // ---- Socket ----

  handleSocketEvent = (e: ISocketEvents<IGameSocketEvents>) => {
    if (e.$) {
      this.handleLifecycleEvent(e);
      return;
    }

    const prevState = this.game;
    if (!prevState) return;

    if (e.t === 'N') return;

    let nextState: IGame;
    try {
      nextState = reduceGame(prevState, e);
    } catch (err) {
      if (e.t === 'M') {
        addError(
          new Error("Delta Error: can't apply move, refetching", {
            cause: err,
          }),
          { gameId: this.gameId, mv: e.mv, currentFen: prevState.currentFen },
        );
        this.props.onRefetch?.();
        return;
      }
      throw err;
    }

    this.queryClient.setQueryData(COMMON_QUERY_KEYS.game(this.gameId), nextState);
    this.game = nextState;

    // ВАЖНО: сравниваем состояния, а не e.t === 'M' — ход может прийти внутри
    // батча ('B'), например мат приходит как [M, F]
    const hasMovesChanged = nextState.moves.length > prevState.moves.length;

    if (hasMovesChanged) {
      this.handleServerMove(nextState);
    }
    const hasGameStarted = prevState.status === 'NotStarted' && nextState.status === 'InProgress';

    if (this.isRealtime && (hasMovesChanged || hasGameStarted)) {
      this.lastMoveReceivedTime = Date.now();
    }

    this.isRealtime = true;
  };

  private handleLifecycleEvent(e: Extract<ISocketEvents<IGameSocketEvents>, { $: string }>) {
    switch (e.$) {
      case 'reconnecting':
      case 'closed': {
        const game = this.game;

        if (e.$ === 'closed' && game?.status === 'Finished') break;

        // Игра не найдена в Redis, а значит скорее всего завершена. Нужно обновить по REST
        if (e.$ === 'closed' && e.code === 4404) {
          this.props.onRefetch?.();

          const err = new Error('Socket did not find the game. Refetching.');
          err.name = 'GameSocket404';
          addError(err, { gameId: this.gameId, playerId: this.props.playerId });

          break;
        }

        this.state.socketProblems = true;

        const isActivePlayer = this.isPlaying && game?.result === 'InProgress';

        if (isActivePlayer && game) {
          const socketError = new Error(
            `Socket ${e.$ === 'closed' ? 'closed' : 'reconnecting'} during active game`,
          );
          socketError.name =
            e.$ === 'closed' ? 'GameSocketClosedError' : 'GameSocketReconnectingError';

          const { moveTimeStampMs, ...timings } = calculateMoveTimings(
            game,
            new Date(),
            this.lastMoveReceivedTime,
          );

          addError(socketError, {
            gameId: this.gameId,
            playerId: this.props.playerId,
            fen: game.currentFen,
            movesCount: game.moves.length,
            errorTimestampMs: moveTimeStampMs,
            ...timings,
          });
        }
        break;
      }
      case 'connected':
        this.isRealtime = false;
        this.lastMoveReceivedTime = null;
        this.state.socketProblems = false;
        break;
      case 'reconnected':
        this.isRealtime = false;
        this.lastMoveReceivedTime = null;
        this.state.socketProblems = false;
        break;
    }
  }

  // ---- Player Move ----

  handlePlayerMove = (m: MoveInfo, _san: string, fen: string) => {
    this.state.fen = fen;
    this.state.lastMove = m;
    this.sendMove(m);
  };

  handlePremove = (m: MoveInfo | null) => {
    this.state.premove = m;
  };

  // ---- Send Move ----

  private sendMove(move: MoveInfo) {
    if (!this.game) return;

    const fenBefore = this.game.currentFen;

    const deadlineTimeout = setTimeout(() => {
      this.state.problemMoveFen = fenBefore;
    }, MOVE_REQUEST_PROBLEMS_THRESHOLD);

    const requestStartTime = new Date();
    const timings = calculateMoveTimings(this.game, requestStartTime, this.lastMoveReceivedTime);

    const req = {
      id: this.gameId,
      move: {
        from: move.from,
        to: move.to,
        promoteTo: move.promotion,
        currentFen: fenBefore,
        timings,
      },
      deadlineMs: MOVE_REQUEST_DEADLINE,
    };

    this.moveFn(req, {
      onSuccess: (data) => {
        if (isNewerState(this.game, data)) {
          this.queryClient.setQueryData(COMMON_QUERY_KEYS.game(this.gameId), data);
        }
      },
      onError: (e) => {
        const requestEndDate = new Date();

        const isStale = this.game && this.game.currentFen !== req.move.currentFen;

        const serverMessage = e instanceof ApiError ? e.serverMessage : null;
        const isGameFinished = !!serverMessage && /^Game .+ is finished$/.test(serverMessage);

        if (!isStale && !isGameFinished) {
          this.props.onError?.(e);
          this.syncFromGame(this.game);
        }

        const customError = new Error(serverMessage ?? e.message, { cause: e });
        customError.name = isGameFinished
          ? 'GameFinishedMoveError'
          : isStale
            ? 'StaleMoveError'
            : 'CantMoveError';

        addError(customError, {
          req,
          timeSyncDelta: TimeSyncService.instance.delta,
          requestStartTime,
          requestEndDate,
          currentFen: this.game?.currentFen,
        });

        this.props.onRefetch?.();
      },
      onSettled: () => {
        clearTimeout(deadlineTimeout);
        this.state.problemMoveFen =
          this.state.problemMoveFen === fenBefore ? null : this.state.problemMoveFen;
      },
    });
  }

  // ---- Server Move ----

  private handleServerMove(d: IGame) {
    const lastMove = d.moves[d.moves.length - 1];

    if (!lastMove) {
      this.state.fen = d.currentFen;
      this.state.lastMove = null;
      return;
    }

    const { from, to, fenAfter, promotedTo } = lastMove;

    this.state.fen = fenAfter;
    this.state.lastMove = {
      from: from as SquareName,
      to: to as SquareName,
      promotion: (promotedTo as PromotionPiece) ?? undefined,
    };

    // Execute premove
    const premove = this.state.premove;
    if (premove) {
      try {
        const r = new Chess(fenAfter, { enable960: true }).move(premove);

        this.state.fen = r.after;
        this.state.lastMove = premove;
        this.sendMove(premove);
      } catch {
        // invalid premove — ignore
      } finally {
        this.state.premove = null;
      }
    }
  }

  // ---- Private ----

  private syncFromGame(d: IGame) {
    this.state.fen = d.currentFen;
    const last = d.moves[d.moves.length - 1];
    this.state.lastMove = last
      ? {
          from: last.from as SquareName,
          to: last.to as SquareName,
          promotion: (last.promotedTo as PromotionPiece) ?? undefined,
        }
      : null;
  }
}

/*
    ____,-------------------------------,____
    \   |            Helpers           |   /
    /___|-------------------------------|___\
*/

function isNewerState(prev: IGame | undefined, next: IGame): boolean {
  // seq=1 — всегда новая NotStarted игра, seq=2 — старт игры, поэтому fallback на 1 безопасен
  return (prev?.seq ?? 1) < next.seq;
}

function getSide(playerId?: string, data?: IGame): 'none' | 'black' | 'white' {
  if (playerId === data?.white.id) return 'white';
  if (playerId === data?.black.id) return 'black';
  return 'none';
}

function getWinner(data?: IGame): ChessWinner | undefined {
  if (data?.result === 'BlackWin') return 'black';
  if (data?.result === 'WhiteWin') return 'white';
  if (data?.result === 'Draw') return 'draw';
  return undefined;
}

function calculateMoveTimings(
  game: IGame,
  requestStartDate: Date,
  lastMoveReceivedTime: number | null,
): components['schemas']['MoveTimings'] {
  const now = requestStartDate.getTime();

  const side = game.nextMoveBy;
  const lastMove = game.moves[game.moves.length - 1];
  const clocks = lastMove?.clockAfter ?? game.clock;
  const timestamp =
    lastMove?.timestampMs ?? (game.status === 'InProgress' ? game.timestampMs : null);

  function calcTime(color: 'White' | 'Black') {
    const timeLeft = color === 'White' ? clocks.whiteLeftMs : clocks.blackLeftMs;

    const syncedTimestamp =
      timestamp && side === color ? TimeSyncService.instance.toClientTimestamp(timestamp) : null;

    const elapsed = syncedTimestamp ? now - syncedTimestamp : 0;

    return timeLeft - elapsed;
  }

  return {
    moveTimeStampMs: now,
    lastMoveReceivedTimeStampMs: lastMoveReceivedTime,
    whiteClockTimeLeftMs: calcTime('White'),
    blackClockTimeLeftMs: calcTime('Black'),
  };
}

function useGamesSocket({
  mode,
  gameId,
  seq = 0,
  customToken,
  enabled = true,
  cb,
}: {
  mode: 'tournament' | 'fast-game' | 'spectator';
  gameId: string;
  seq?: number;
  customToken?: string;
  enabled?: boolean;
  cb: (e: ISocketEvents<IGameSocketEvents>) => void;
}) {
  const deviceId = useDeviceId();
  const socketRef = useRef<GamesSocket | null>(null);
  const cbRef = useRef(cb);
  cbRef.current = cb;
  const seqRef = useRef(seq);
  seqRef.current = seq;

  if (!enabled) {
    socketRef.current?.discard();
    socketRef.current = null;
  } else if (!socketRef.current || socketRef.current.gameId !== gameId) {
    socketRef.current?.discard();
    const socket = new GamesSocket(gameId, deviceId, mode, () => seqRef.current, customToken);
    socket.subscribe((e) => {
      if (!e.$ && 'sq' in e) {
        seqRef.current = Math.max(seqRef.current, e.sq);
      }
      cbRef.current(e);
    });
    socketRef.current = socket;
  }

  useEffect(
    () => () => {
      socketRef.current?.discard();
    },
    [],
  );
}
